import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate as formatDateOnly, parseLocalDate } from "@ui/shared";
import { useRenewal } from "./RenewalContext";
import RenewalSteps from "./RenewalSteps";
import ContactStep from "./steps/ContactStep";
import MoveOutTips from "./steps/MoveOutTips";
import MovingCostsModal from "./steps/MovingCostsModal";
import { useStepParam } from "./useStepParam";
import { useRenewalSubmit } from "./useRenewalSubmit";
import PmContactCard from "./steps/PmContactCard";
import {
  EARLY_MOVE_OUT_NOTICE,
  LATE_MOVE_OUT_NOTICE,
  NOTICE_30_DAY,
  IMPORTANT_LEAD,
} from "./renewalCopy";
import {
  Card,
  CountedTextarea,
  Notice,
  ReviewRow,
  StepHeading,
  StepNav,
  YesNo,
} from "./renewalUi";

// "Extras" (the upsell step) is intentionally left out for now.
const STEPS = ["Move-Out", "Contact", "Condition", "Review"];

const DESC_MAX = 500;
const MAX_FILES = 3;

const fmt = (d: string | null | undefined) =>
  d
    ? formatDateOnly(d, { year: "numeric", month: "long", day: "numeric" })
    : "—";

/**
 * "I'm Moving Out" — Move-Out Date → Contact → Condition → Review.
 */
const MoveOutFlow: React.FC = () => {
  const navigate = useNavigate();
  const { termUuid, policy, draft, update } = useRenewal();
  const { step, done: submitted, goTo, finish } = useStepParam(STEPS.length);
  const { submit, submitting, error: submitError } = useRenewalSubmit(finish);
  const [showCosts, setShowCosts] = useState(false);

  const leaseEnd = policy?.currentTerm?.endsOn ?? policy?.endsOn ?? null;

  const back = () =>
    step === 1 ? navigate(`/renewal/${termUuid}`) : goTo(step - 1);
  const next = () => goTo(step + 1);

  // Compare as local-midnight calendar days so the notice doesn't flip a day
  // early/late for tenants in US timezones.
  const timing = (() => {
    if (!draft.moveOutDate || !leaseEnd) return null;
    const chosen = parseLocalDate(draft.moveOutDate).getTime();
    const end = parseLocalDate(leaseEnd).getTime();
    if (Number.isNaN(chosen) || Number.isNaN(end)) return null;
    if (chosen < end) return "early" as const;
    if (chosen > end) return "late" as const;
    return "exact" as const;
  })();

  const setCondition = (patch: Partial<typeof draft.condition>) =>
    update({ condition: { ...draft.condition, ...patch } });

  const conditionAnswered =
    draft.condition.hasDamage === false ||
    (draft.condition.hasDamage === true &&
      draft.condition.description.trim().length > 0);

  if (submitted) {
    return (
      <div className="text-center">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-success/10">
          <svg
            className="h-7 w-7 text-success"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-primary">Thank you!</h2>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-gray-600">
          We've notified your property manager. If you have additional
          questions, please contact them directly.
        </p>

        <div className="mt-6 text-left">
          <MoveOutTips />
        </div>

        <PmContactCard />

        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="mt-6 cursor-pointer rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light"
        >
          Back to dashboard
        </button>
      </div>
    );
  }

  return (
    <div>
      <RenewalSteps steps={STEPS} current={step} badge="move-out" />

      {step === 1 && (
        <>
          <StepHeading
            title="Thank you for letting us know!"
            subtitle="This helps you & your PM avoid expensive surprises at move-out."
          />

          <Card className="mb-4 bg-sky-50">
            <p className="text-xs font-semibold uppercase tracking-wide text-sky-900">
              Current lease end date
            </p>
            <p className="mt-1 text-lg font-bold text-sky-900">
              {fmt(leaseEnd)}
            </p>
          </Card>

          <Card>
            <label className="mb-1 block text-sm font-semibold text-primary">
              What is your planned move-out date?
            </label>
            <input
              type="date"
              value={draft.moveOutDate}
              onChange={(e) => update({ moveOutDate: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />

            {timing === "early" && (
              <div className="mt-4">
                <Notice tone="warning" title="Early Move-Out Notice">
                  {EARLY_MOVE_OUT_NOTICE.before}
                  <strong className="font-semibold">
                    {EARLY_MOVE_OUT_NOTICE.emphasis}
                  </strong>
                  {EARLY_MOVE_OUT_NOTICE.after}
                </Notice>
              </div>
            )}
            {timing === "late" && (
              <div className="mt-4">
                {/* Red rather than amber: moving out after the lease expires
                    needs written approval and can incur charges, so it's a
                    harder warning than the early notice. */}
                <Notice tone="danger" title="Late Move-Out Notice">
                  {LATE_MOVE_OUT_NOTICE.before}
                  <strong className="font-semibold">
                    {LATE_MOVE_OUT_NOTICE.emphasis}
                  </strong>
                  {LATE_MOVE_OUT_NOTICE.after}
                </Notice>
              </div>
            )}
          </Card>

          {/* The "Did You Know?" cost breakdown now arrives as a modal on
              Continue — see MovingCostsModal for why. */}
          <StepNav
            onBack={back}
            onNext={() => setShowCosts(true)}
            nextDisabled={!draft.moveOutDate}
          />

          <MovingCostsModal
            open={showCosts}
            onContinue={() => {
              setShowCosts(false);
              next();
            }}
            onSeeRenewal={() => {
              setShowCosts(false);
              navigate(`/renewal/${termUuid}/not-sure`);
            }}
          />
        </>
      )}

      {step === 2 && <ContactStep onBack={back} onNext={next} />}

      {step === 3 && (
        <>
          <StepHeading
            title="Any damages or maintenance to report?"
            subtitle="We want your move-out to go smoothly and without surprises. Letting us know now gives your PM time to address anything before your final walk-through — so you're not caught off guard on move-out day."
          />

          <div className="mb-4">
            <MoveOutTips />
          </div>

          <Card>
            <YesNo
              value={draft.condition.hasDamage}
              onChange={(v) =>
                setCondition({
                  hasDamage: v,
                  description: v ? draft.condition.description : "",
                  files: v ? draft.condition.files : [],
                })
              }
            />

            {draft.condition.hasDamage === true && (
              <div className="mt-4">
                <label className="mb-1 block text-sm font-semibold text-primary">
                  Please describe ({DESC_MAX} characters max)
                </label>
                <CountedTextarea
                  value={draft.condition.description}
                  onChange={(description) => setCondition({ description })}
                  maxLength={DESC_MAX}
                  placeholder="Describe any damages or outstanding maintenance requests..."
                />

                <p className="mb-1 mt-4 text-sm font-semibold text-primary">
                  Attach photos or documents (optional, up to {MAX_FILES})
                </p>

                {/* The native file input renders as unstyled browser chrome
                    ("Choose files / No file chosen") that doesn't read as
                    clickable. Hide it and drive it from a styled label — the
                    label still opens the picker, including via keyboard. */}
                {draft.condition.files.length < MAX_FILES && (
                  <label className="flex cursor-pointer flex-col items-center gap-1 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50/60 px-4 py-5 text-center transition-colors hover:border-primary/40 hover:bg-primary/5 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
                    <span className="flex items-center gap-2 text-sm font-semibold text-primary">
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Choose files
                    </span>
                    <span className="text-xs text-gray-500">
                      Images or PDF ·{" "}
                      {MAX_FILES - draft.condition.files.length} remaining
                    </span>
                    <input
                      type="file"
                      multiple
                      accept="image/*,.pdf"
                      // Visually hidden rather than `display:none` so the
                      // input stays in the tab order; the label shows a focus
                      // ring via focus-within.
                      className="sr-only"
                      onChange={(e) => {
                        const picked = Array.from(e.target.files ?? []);
                        setCondition({
                          files: [...draft.condition.files, ...picked].slice(
                            0,
                            MAX_FILES,
                          ),
                        });
                        // Clear so re-picking the same file still fires change.
                        e.target.value = "";
                      }}
                    />
                  </label>
                )}

                {draft.condition.files.length > 0 && (
                  <ul className="mt-2 space-y-1.5">
                    {draft.condition.files.map((f, i) => (
                      <li
                        key={`${f.name}-${i}`}
                        className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                      >
                        <span className="truncate text-gray-700">{f.name}</span>
                        <button
                          type="button"
                          aria-label={`Remove ${f.name}`}
                          onClick={() =>
                            setCondition({
                              files: draft.condition.files.filter(
                                (_, idx) => idx !== i,
                              ),
                            })
                          }
                          className="shrink-0 cursor-pointer text-xs font-medium text-danger hover:underline"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </Card>

          <StepNav
            onBack={back}
            onNext={next}
            nextDisabled={!conditionAnswered}
          />
        </>
      )}

      {step === 4 && (
        <>
          <StepHeading
            title="Review & Submit"
            subtitle="Double-check everything before it goes to your PM."
          />

          <Card className="p-0">
            <ReviewRow label="Intent" value="Moving Out" />
            <ReviewRow label="Lease End Date" value={fmt(leaseEnd)} />
            <ReviewRow
              label="Planned Move-Out"
              value={fmt(draft.moveOutDate)}
            />
            <ReviewRow label="Name" value={draft.contact.name} />
            <ReviewRow label="Email" value={draft.contact.email} />
            <ReviewRow label="Phone" value={draft.contact.phone} />
            <ReviewRow
              label="Damages Reported"
              value={
                draft.condition.hasDamage
                  ? draft.condition.description
                  : "None reported"
              }
            />
            {draft.condition.files.length > 0 && (
              <ReviewRow
                label="Attachments"
                value={draft.condition.files.map((f) => f.name).join(", ")}
              />
            )}
            <div className="px-5 py-3">
              <button
                type="button"
                onClick={() => goTo(1)}
                className="cursor-pointer text-sm font-semibold text-primary underline"
              >
                ← Make Changes
              </button>
            </div>
          </Card>

          <div className="mt-4">
            <Notice tone="warning">
              <strong>{IMPORTANT_LEAD}</strong> {NOTICE_30_DAY}
            </Notice>
          </div>

          {submitError && (
            <p className="mb-3 text-sm font-medium text-danger">{submitError}</p>
          )}

          <StepNav
            onBack={back}
            onNext={() => submit("move-out")}
            nextLabel={
              submitting ? "Submitting…" : "Submit to Property Manager ✓"
            }
            nextDisabled={submitting}
            nextVariant="success"
          />
        </>
      )}
    </div>
  );
};

export default MoveOutFlow;
